import { supabase } from './supabase';
import { stripeProducts } from '../stripe-config';
import type { SubscriptionTierCode } from '../types';

export async function createCheckoutSession(tier: SubscriptionTierCode) {
  try {
    // Get the current user's session
    const { data: { session } } = await supabase.auth.getSession();
    
    if (!session) {
      throw new Error('Authentication required');
    }
    
    // Get the product information
    const product = stripeProducts[tier];
    if (!product || tier === 'free') {
      throw new Error('Invalid subscription tier');
    }
    
    // Get the current URL for success/cancel redirects
    const baseUrl = window.location.origin;
    const successUrl = `${baseUrl}/subscription?success=true&tier=${tier}`;
    const cancelUrl = `${baseUrl}/subscription?canceled=true`;
    
    // Create checkout session
    const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/stripe-checkout`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`
      },
      body: JSON.stringify({
        price_id: product.priceId,
        success_url: successUrl,
        cancel_url: cancelUrl,
        mode: 'subscription'
      })
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to create checkout session');
    }
    
    const { url } = await response.json();
    return url;
  } catch (error) {
    console.error('Error creating checkout session:', error);
    throw error;
  }
}

export async function getSubscriptionStatus() {
  try {
    // Get the current user's session
    const { data: { session } } = await supabase.auth.getSession();
    
    if (!session) {
      return null;
    }
    
    // Get the user's subscription status
    const { data, error } = await supabase
      .from('stripe_user_subscriptions')
      .select('*')
      .limit(1)
      .single();
    
    if (error) {
      // If no subscription is found, return null instead of throwing an error
      if (error.code === 'PGRST116') {
        return null;
      }
      console.error('Error fetching subscription status:', error);
      return null;
    }
    
    return data;
  } catch (error) {
    console.error('Error getting subscription status:', error);
    return null;
  }
}

export async function getOrderHistory() {
  try {
    // Get the current user's session
    const { data: { session } } = await supabase.auth.getSession();
    
    if (!session) {
      return [];
    }
    
    // Get the user's order history
    const { data, error } = await supabase
      .from('stripe_user_orders')
      .select('*')
      .order('order_date', { ascending: false });
    
    if (error) {
      console.error('Error fetching order history:', error);
      return [];
    }
    
    return data || [];
  } catch (error) {
    console.error('Error getting order history:', error);
    return [];
  }
}